import React, { useState, useEffect } from 'react';
import { IntegrationWrapper } from './types';

interface IntegrationItemProps {
  integration: IntegrationWrapper;
  onConfigure: (integration: IntegrationWrapper) => void;
  onStatusToggle: (id: number, currentStatus: boolean) => void;
  onCustomerIntegrationToggle?: (customerIntegrationId: number, currentStatus: boolean) => void;
  showStatusToggle?: boolean;
}

 

const IntegrationItem: React.FC<IntegrationItemProps> = ({
  integration,
  onConfigure,
  onStatusToggle,
  onCustomerIntegrationToggle,
  showStatusToggle = true
}) => {

  // Responsive: track mobile layout
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // For integrations that allow multiple instances, show individual configurations
  if (integration.integration.AllowMultiple && integration.customerIntegrations && integration.customerIntegrations.length > 0) {
    return (
      <div style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        {/* Main integration header */}
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid #f0f0f0'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img
              src={integration.integration.Icon}
              alt={integration.integration.Name}
              style={{ width: 40, height: 40, marginRight: 16 }}
            />
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0, marginBottom: 4 }}>{integration.integration.Name}</h4>
              {!isMobile && <p style={{ margin: 0, color: '#666', fontSize: 14 }}>{integration.integration.Description}</p>}
            </div>
            <button
              onClick={() => onConfigure(integration)}
              disabled={!integration.integration.Enabled}
              style={{
                padding: '6px 12px',
                background: !integration.integration.Enabled ? '#f0f0f0' : 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                color: !integration.integration.Enabled ? '#666' : 'white',
                border: 'none',
                borderRadius: 4,
                cursor: integration.integration.Enabled ? 'pointer' : 'not-allowed',
                opacity: integration.integration.Enabled ? 1 : 0.8
              }}
            >
              {integration.integration.Enabled ? 'Manage Instances' : 'Coming Soon'}
            </button>
          </div>

          {isMobile && 
            <div style={{ marginTop: 12 }}>
              <p style={{ margin: 0, color: '#666', fontSize: 14 }}>{integration.integration.Description}</p>
            </div>
          }
        </div>

        {/* Individual customer integrations */}
        <div style={{ padding: '8px 16px 16px 16px' }}>
          {integration.customerIntegrations.map((customerIntegration, index) => (
            <div
              key={customerIntegration.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: '#f8f9fa',
                borderRadius: 4,
                marginBottom: index < integration.customerIntegrations!.length - 1 ? 8 : 0
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>
                  Instance {index + 1}
                </div>
                {(customerIntegration.configValues && customerIntegration.configValues.projectName) &&                 
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Project: {customerIntegration.configValues.projectName}
                  </div>
                }
                {(customerIntegration.configValues && customerIntegration.configValues.instance) &&                 
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Name: {customerIntegration.configValues.instance}
                  </div>
                }
              </div>

              {onCustomerIntegrationToggle && (
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: 50, height: 24 }}>
                  <input
                    type="checkbox"
                    checked={customerIntegration.isEnabled}
                    onChange={() => onCustomerIntegrationToggle(customerIntegration.id, customerIntegration.isEnabled)}
                    disabled={!integration.integration.Enabled}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      cursor: integration.integration.Enabled ? 'pointer' : 'not-allowed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: customerIntegration.isEnabled ? '#2196F3' : '#ccc',
                      transition: '.4s',
                      borderRadius: 24,
                      opacity: customerIntegration.isEnabled ? 1 : 0.5,
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        content: '',
                        height: 16,
                        width: 16,
                        left: customerIntegration.isEnabled ? 'calc(100% - 20px)' : 4,
                        bottom: 4,
                        backgroundColor: 'white',
                        transition: '.4s',
                        borderRadius: '50%',
                      }}
                    />
                  </span>
                </label>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // For single instance integrations, show the original layout
  return (
    <div
      key={integration.integration.Id}
      style={{
        padding: 16,
        backgroundColor: 'white',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <img
          src={integration.integration.Icon}
          alt={integration.integration.Name}
          style={{ width: 40, height: 40, marginRight: 16 }}
        />
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: 0, marginBottom: 4 }}>{integration.integration.Name}</h4>
          {!isMobile && <p style={{ margin: 0, color: '#666', fontSize: 14 }}>{integration.integration.Description}</p>}
        </div>
        <div style={{ 
          display: 'flex', 
          //alignItems: 'center', 
          gap: 16,
          //flexDirection: isMobile ? 'column-reverse' : 'row'
          flexDirection: 'column-reverse',
          justifyContent: 'flex-end'
        }}>
          {/* Status toggle */}
          {integration.configured &&
            <div style={{ width: '100%', display: 'flex', justifyContent: isMobile ? 'flex-end' : 'flex-end' }}>
              {showStatusToggle ? (
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: 50, height: 24 }}>
                  <input
                    type="checkbox"
                    checked={integration.isStatusEnabled}
                    onChange={() => onStatusToggle(integration.integration.Id, integration.isStatusEnabled)}
                    disabled={!integration.configured || !integration.integration.Enabled}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      cursor: integration.integration.Enabled ? 'pointer' : 'not-allowed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: integration.isStatusEnabled ? '#2196F3' : '#ccc',
                      transition: '.4s',
                      borderRadius: 24,
                      opacity: integration.isStatusEnabled ? 1 : 0.5,
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        content: '',
                        height: 16,
                        width: 16,
                        left: integration.isStatusEnabled ? 'calc(100% - 20px)' : 4,
                        bottom: 4,
                        backgroundColor: 'white',
                        transition: '.4s',
                        borderRadius: '50%',
                      }}
                    />
                  </span>
                </label>
              ) : (
                integration.customerIntegrations && integration.customerIntegrations.length > 0 &&
                <div
                  onClick={() => onConfigure(integration)}
                  title="Click to manage individual instances"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 8px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: 4,
                    fontSize: 12,
                    color: '#1976d2',
                    cursor: 'pointer',
                    height: 24,
                    boxSizing: 'border-box',
                    border: '1px solid transparent',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbdefb';
                    e.currentTarget.style.borderColor = '#1976d2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >

                  <span>
                    {integration.customerIntegrations && integration.customerIntegrations.length > 0 ?
                      `${integration.customerIntegrations.filter(ci => ci.isEnabled).length}/${integration.customerIntegrations.length} enabled` :
                      '0/0 enabled'
                    }
                  </span>

                </div>
              )}
            </div>
          }

          {/* Edit button */}
          <button
            onClick={() => onConfigure(integration)}
            disabled={!integration.integration.Enabled}
            style={{
              padding: '6px 12px',
              background: !integration.integration.Enabled ? '#f0f0f0' : 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
              color: !integration.integration.Enabled ? '#666' : 'white',
              border: 'none',
              borderRadius: 4,
              cursor: integration.integration.Enabled ? 'pointer' : 'not-allowed',
              opacity: integration.integration.Enabled ? 1 : 0.8
            }}
          >
            {integration.integration.Enabled ?
              (integration.configured ? isMobile ? 'Edit' : 'Edit Configuration' : 'Configure') :
              'Coming Soon'}
          </button>
        </div>
      </div>

      {isMobile && 
        <div style={{ marginTop: 12 }}>
          <p style={{ margin: 0, color: '#666', fontSize: 14 }}>{integration.integration.Description}</p>
        </div>
      }
    </div>
  );
};

export default IntegrationItem;
